import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Phone, 
  RefreshCw as RotateCcw, 
  Shield as ShieldCheck,
  Clipboard as ClipboardCheck,
  AlertTriangle, MessageCircle, CirclePlus,
  Copy, CheckCircle, Wallet
} from "lucide-react";


const SMS = ({ darkMode }) => {
  const API_KEY = "e9d5649f-cd71-428d-a7ce-276e35e55810";
  const PROJECT_ID = 1063;

  const [specifiedNumber, setSpecifiedNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [numbers, setNumbers] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [processingActions, setProcessingActions] = useState({});
  const [pollingIntervals, setPollingIntervals] = useState({});
  const [copySuccessPosition, setCopySuccessPosition] = useState({ visible: false, top: 0, left: 0 });
  const [balance, setBalance] = useState(null);
  const [loadingBalance, setLoadingBalance] = useState(false);
  
  const [copiedItem, setCopiedItem] = useState(null); // 記錄哪個項目被複製了

  // 複製函數實現
  const handleCopy = (text, id, type) => {
    // 複製文字到剪貼簿
    navigator.clipboard.writeText(text)
      .then(() => {
        // 設置被複製的項目ID和類型
        setCopiedItem({ id, type });
        
        // 3秒後自動隱藏提示
        setTimeout(() => {
          setCopiedItem(null);
        }, 2000);
      })
      .catch(err => {
        console.error('複製失敗:', err);
      });
  };

  // 号码数据结构
  const initialNumberState = (phone) => ({
    phone,
    code: "",
    polling: false,
    status: "active" // active/released/blacklisted
  });

  // 獲取餘額信息
  const fetchBalance = async () => {
    setLoadingBalance(true);
    try {
      const response = await fetch("https://robot-sms.xyz/api/v1/user/info", {
        method: "GET",
        headers: { "Authorization": API_KEY }
      });
      
      const data = await response.json();
      if (data.code === 200 && data.data && data.data.balance !== undefined) {
        // 將餘額轉換為小數格式 (最後三位為小數)
        const rawBalance = data.data.balance;
        const formattedBalance = (rawBalance / 1000).toFixed(3);
        setBalance(formattedBalance);
      } else {
        setError("獲取餘額失敗");
      }
    } catch (err) {
      console.error("獲取餘額錯誤", err);
      setError("網絡請求失敗");
    }
    setLoadingBalance(false);
  };

  // 组件加载时获取余额
  useEffect(() => {
    fetchBalance();
  }, []);

  // 获取号码
  const getNumber = async () => {
    setLoading(true);
    setError("");
    try {
      // 加入1秒延遲
      await new Promise(resolve => setTimeout(resolve, 1000));
		
      const params = { project_id: PROJECT_ID, is_long_task: false, index: 0 };
      if (/^\d{8}$/.test(specifiedNumber)) params.phone_number = specifiedNumber;

      const response = await fetch("https://robot-sms.xyz/api/v1/core/task/create", {
        method: "POST",
        headers: { "Authorization": API_KEY, "Content-Type": "application/json" },
        body: JSON.stringify(params)
      });

      const data = await response.json();
      if (data.code === 200 && data.data.phone_number) {
        setSuccessMessage("號碼獲取成功");
        const newNumber = initialNumberState(data.data.phone_number);
        setNumbers(prev => {
          const updated = [newNumber, ...prev.slice(0, 9)]; // 保留最新10个
          return updated;
        });
        startPolling(data.data.phone_number);
        // 每次獲取號碼後更新餘額
        fetchBalance();
      } else {
        setError(data.msg || "獲取號碼失敗");
      }
    } catch (err) {
      setError("網絡請求失敗");
    }
    setLoading(false);
  };


  // 开始轮询验证码
  const startPolling = (number) => {
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`https://robot-sms.xyz/api/v1/core/task/get/${number}/${PROJECT_ID}`, {
          headers: { "Authorization": API_KEY }
        });
        const data = await response.json();
        
        if (data.code === 200) {
          if (data.data.code) {
            setNumbers(prev => prev.map(n => 
              n.phone === number ? { ...n, code: data.data.code, polling: false } : n
            ));
            // 成功获取时清除定时器
            clearInterval(pollingIntervals[number]);
            setPollingIntervals(prev => {
              const newIntervals = { ...prev };
              delete newIntervals[number];
              return newIntervals;
            });
          }
        }
      } catch (err) {
        console.error("輪詢異常");
      }
    }, 3500); // 固定3.5秒间隔

    // 保存定时器引用
    setPollingIntervals(prev => ({
      ...prev,
      [number]: interval
    }));

    // 更新轮询状态
    setNumbers(prev => prev.map(n => 
      n.phone === number ? { ...n, polling: true } : n
    ));
  };
  
  // 复制到剪贴板
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };
  
  
  // 操作处理函数
  const handleNumberAction = async (phone, action) => {
    try {
      // 清除该号码的轮询定时器
      if (pollingIntervals[phone]) {
        clearInterval(pollingIntervals[phone]);
        setPollingIntervals(prev => {
          const newIntervals = { ...prev };
          delete newIntervals[phone];
          return newIntervals;
        });
      }

      await fetch("https://robot-sms.xyz/api/v1/core/task/" + action, {
        method: "PUT",
        headers: { "Authorization": API_KEY, "Content-Type": "application/json" },
        body: JSON.stringify({ project_id: PROJECT_ID, phone_number: phone })
      });
      setSuccessMessage("成功對該號碼進行操作");
      setNumbers(prev => prev.filter(n => n.phone !== phone));
      
      // 操作後更新餘額
      fetchBalance();
    } catch (err) {
      setError("操作失敗");
    }
  };
	
  // 成功提示自動消失
  useEffect(() => {
    const timer = setTimeout(() => {
      setSuccessMessage("");
    }, 2000);
    return () => clearTimeout(timer);
  }, [successMessage]);
  
  // 錯誤提示自動消失邏輯加強
  useEffect(() => {
    let timer
    if (error) {
      timer = setTimeout(() => {
        setError("") // 確保狀態清理
      }, 5000)
    }
    return () => {
      if (timer) clearTimeout(timer) // 明確清除定時器
    }
  }, [error])
	
  // 添加组件卸载时的清理
  useEffect(() => {
    return () => {
      // 清除所有剩余定时器
      Object.values(pollingIntervals).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, [pollingIntervals]);

  return (
    <motion.div
      className={`p-6 rounded-xl shadow-2xl ${darkMode ? "bg-[#1E1E1E]" : "bg-white"}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      {/* 標題區域 */}
      <div className="flex items-start justify-between mb-8">
        <div className="flex items-start space-x-4">
          <div className="p-3 rounded-xl bg-gradient-to-r from-purple-500 to-blue-500">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent">
              SMS 驗證管理
            </h2>
            <p className={`mt-1 text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
              即時獲取與管理短信驗證碼
            </p>
          </div>
        </div>
        
        {/* 餘額顯示區域 - 移到右上角 */}
        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`p-3 rounded-xl border ${
            darkMode 
              ? "border-purple-800/30 bg-purple-900/10" 
              : "border-purple-100 bg-purple-50"
          }`}
          style={{ maxWidth: "fit-content" }}
        >
          <div className="flex items-center">
            <div className={`p-2 rounded-md ${
              darkMode ? "bg-purple-800/20" : "bg-purple-100"
            }`}>
              <Wallet size={16} className={darkMode ? "text-purple-400" : "text-purple-500"} />
            </div>
            <div className="ml-3">
              <p className={`text-xs font-medium ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                賬戶餘額
              </p>
              <div className="flex items-center mt-0.5">
                {loadingBalance ? (
                  <div className="flex items-center">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ repeat: Infinity, duration: 1 }}
                      className={`w-3 h-3 border-2 rounded-full ${
                        darkMode ? "border-purple-400/40" : "border-purple-500/50"
                      } border-t-transparent mr-2`}
                    />
                    <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                      加載中...
                    </span>
                  </div>
                ) : (
                  <motion.div
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    className={`font-mono text-lg font-bold ${
                      darkMode 
                        ? "bg-gradient-to-r from-purple-400 to-indigo-400 bg-clip-text text-transparent" 
                        : "bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent"
                    }`}
                  >
                    {balance !== null ? `$${balance}` : "--"}
                  </motion.div>
                )}
                
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={fetchBalance}
                  className={`ml-2 p-1 rounded-full ${
                    darkMode 
                      ? "bg-purple-800/20 hover:bg-purple-800/30 text-purple-400" 
                      : "bg-purple-100 hover:bg-purple-200 text-purple-500"
                  }`}
                  disabled={loadingBalance}
                >
                  <RotateCcw size={12} className={loadingBalance ? "animate-spin" : ""} />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="space-y-6">
        {/* 輸入行修改 */}
        <div className="flex flex-col space-y-4">
          <div className="w-full">
            <label className={`block text-sm mb-2 ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
              <span className="flex items-center gap-1.5">
                <ClipboardCheck size={16} />
                指定號碼（選填）
              </span>
            </label>
            
            <div className="flex items-center gap-4"> {/* 使用gap控制间距 */}
              {/* 输入框 */}
              <motion.div 
                className="relative"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <input
                  type="text"
                  value={specifiedNumber}
                  onChange={(e) => setSpecifiedNumber(e.target.value)}
                  className={`px-4 py-2.5 rounded-xl border-2 transition-all
                    ${darkMode 
                      ? "bg-[#2C2C2C] border-[#404040] focus:border-purple-400 text-gray-200" 
                      : "bg-gray-50 border-gray-200 focus:border-purple-400 text-gray-700"}
                    shadow-sm hover:shadow-md focus:shadow-lg`}
                  placeholder="留空隨機獲取"
                  style={{ 
                    width: "240px", // 精确8位数字宽度
                  }}
                />
              </motion.div>

              {/* 按钮 */}
              <motion.button
                onClick={getNumber}
                disabled={loading}
                className={`px-6 py-2.5 rounded-xl font-medium flex items-center justify-center
                  ${darkMode
                    ? "bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500/90 hover:to-blue-500/90"
                    : "bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-400/90 hover:to-blue-400/90"}
                  shadow-lg transition-all duration-300 relative overflow-hidden`}
                style={{ width: "160px" }} // 固定按钮宽度
              >
                <div className="absolute inset-0 bg-white/10 opacity-0 hover:opacity-10 transition-opacity" /> {/* 悬停遮罩层 */}

                <motion.div 
                  className="flex items-center gap-2 w-full justify-center"
                  initial={false}
                  animate={{
                    opacity: loading ? 0 : 1,
                    transition: { duration: 0.2 }
                  }}
                >
                  <motion.div
                    whileHover={{ scale: 1.1 }}
                    className="p-1 rounded-full bg-white/10"
                  >
                    <CirclePlus size={18} className="shrink-0" />
                  </motion.div>
                  <span>獲取號碼</span>
                </motion.div>

                <motion.div 
                  className="flex items-center gap-2 w-full justify-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: loading ? 1 : 0,
                    transition: { duration: 0.2 }
                  }}
                >
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1 }}
                    className={`w-5 h-5 border-2 rounded-full ${
                      darkMode ? "border-white/40" : "border-white/50"
                    } border-t-transparent`}
                  />
                  <span>獲取中...</span>
                </motion.div>
              </motion.button>
            </div>
          </div>
        </div>
        
        {/* 成功提示 */}
        <AnimatePresence>
          {successMessage && (
            <motion.div
              key="success"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 50 }}
              className={`fixed top-6 right-6 px-4 py-2.5 rounded-md flex items-center gap-2 ${
                darkMode ? "bg-green-900/30 text-green-400" : "bg-green-100 text-green-600"
              } shadow-lg z-50 backdrop-blur-sm`}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <CheckCircle size={18} className="shrink-0" />
              <span className="font-medium">{successMessage}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* 号码列表 - 驗證碼和等待提示都在號碼右側 */}
        <AnimatePresence>
          {numbers.map((num) => (
            <motion.div
              key={num.phone}
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-4"
            >
              <div className={`p-4 rounded-xl border-2 ${darkMode ? "border-[#363636] bg-[#2C2C2C]" : "border-gray-200 bg-gray-50"}`}>
                <div className="flex items-center justify-between">
                  {/* 左側號碼與驗證碼/等待提示 */}
                  <div className="flex items-center">
                    <div className={`w-3 h-3 rounded-full animate-pulse ${num.code ? "bg-green-400" : "bg-blue-400"} mr-3`} />
                    
                    {/* 號碼部分 */}
                    <div className="relative">
                      {/* 複製提示 */}
                      {copiedItem && copiedItem.id === num.phone && copiedItem.type === "phone" && (
                        <div className={`absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 ${
                          darkMode ? "bg-gray-700 text-green-400" : "bg-gray-100 text-green-600"
                        } text-xs rounded shadow-md whitespace-nowrap`}>
                          已複製
                        </div>
                      )}
                      
                      <div 
                        className="cursor-pointer flex items-center"
                        onClick={() => handleCopy(num.phone, num.phone, "phone")}
                      >
                        <span className="text-sm font-mono bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent font-bold hover:opacity-80 transition-colors">
                          {num.phone}
                        </span>
                      </div>
                    </div>
                    
                    {/* 驗證碼或等待提示 - 放在號碼右側 */}
                    <div className="ml-4">
                      {/* 如果有驗證碼，顯示驗證碼 */}
                      {num.code ? (
                        <div className="relative">
                          {/* 複製提示 */}
                          {copiedItem && copiedItem.id === num.code && copiedItem.type === "code" && (
                            <div className={`absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 ${
                              darkMode ? "bg-gray-700 text-green-400" : "bg-gray-100 text-green-600"
                            } text-xs rounded shadow-md whitespace-nowrap`}>
                              已複製
                            </div>
                          )}
                          
                          <div 
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg cursor-pointer ${
                              darkMode 
                                ? "bg-gradient-to-r from-green-900/30 to-emerald-900/30 hover:from-green-900/40 hover:to-emerald-900/40 border border-green-700/20" 
                                : "bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 border border-green-200"
                            }`}
                            onClick={() => handleCopy(num.code, num.code, "code")}
                          >
                            <CheckCircle size={16} className={darkMode ? "text-green-400" : "text-green-500"} />
                            <span className={`font-mono font-medium ${
                              darkMode 
                                ? "bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent" 
                                : "bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent"
                            }`}>
                              {num.code}
                            </span>
                          </div>
                        </div>
                      ) : (
                        /* 如果正在輪詢但沒有驗證碼，顯示等待提示 */
                        num.polling && (
                          <div className="flex items-center">
                            <motion.div
                              animate={{ rotate: 360 }}
                              transition={{ repeat: Infinity, duration: 1.5, ease: "linear" }}
                              className={`w-4 h-4 mr-2 border-2 rounded-full ${
                                darkMode ? "border-blue-400/30 border-t-blue-400" : "border-blue-300/30 border-t-blue-500"
                              }`}
                            />
                            <p className={`text-sm ${darkMode ? "text-blue-300/70" : "text-blue-500/70"}`}>
                              等待驗證碼中...
                            </p>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* 中間區域：留空 */}
                  <div className="flex-1" />

                  {/* 右侧操作按钮 */}
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => handleNumberAction(num.phone, "release")}
                      className={`px-3 py-1.5 rounded-lg transition-colors ${
                        darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-200 hover:bg-gray-300"
                      }`}
                    >
                      釋放
                    </button>
                    <button
                      onClick={() => handleNumberAction(num.phone, "black")}
                      className={`px-3 py-1.5 rounded-lg transition-colors ${
                        darkMode ? "bg-red-700/80 hover:bg-red-600/80" : "bg-red-500 hover:bg-red-600 text-white"
                      }`}
                    >
                      拉黑
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* 錯誤提示 */}
        <AnimatePresence>
          {error && (
            <motion.div
              key="error"
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -5, scale: 0.9 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
              className={`p-3 rounded-lg flex items-center gap-2 ${
                darkMode ? "bg-red-900/30 text-red-400" : "bg-red-100 text-red-600"
              }`}
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ repeat: 1, duration: 0.4 }}
              >
                <AlertTriangle size={16} />
              </motion.div>
              {error}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default SMS;